import { useEffect, useMemo, useState } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';

import { Button, Center, Flex, HStack, Spinner, useDisclosure, VStack } from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  Badge,
  CheckOutline,
  ModulesConfig,
  ModulesEnableOptions,
  PayCalendar,
  Payday,
  SavingsFeatureConfig,
  useToast,
} from '@aslanpay/portal-common-lib';

import Module from 'app/components/Companies/CreateCompany/CreateCompanyForm/CompanyModulesForm/Module';
import { MODULE_DISPLAY_INFO } from 'app/components/Companies/CreateCompany/CreateCompanyForm/types';
import { companyPaths } from 'app/constants/routes';
import { DigitalVouchersConfigModal } from 'app/components/Settings/DigitalVouchersConfigModal';
import { SavingsConfigModal } from 'app/components/Settings/SavingsConfigModal';
import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchInfinitePayCalendars from 'app/hooks/useFetchPayCalendars/useInfiniteFetchPayCalendars';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';
import useUpdatePayCalendar from 'app/hooks/useUpdatePayCalendar';

import { FeatureModulesTabProps } from './FeatureModulesTab.types';

interface SavingsEffectiveBadgesProps {
  moduleStatus: ModulesEnableOptions;
  config?: SavingsFeatureConfig;
}

function SavingsEffectiveBadges({ moduleStatus, config = undefined }: SavingsEffectiveBadgesProps) {
  const isModuleEnabled = moduleStatus === 'ENABLED';
  const isFlexibleLive = isModuleEnabled && Boolean(config?.flexibleSaverConfig?.enabled);
  const isChallengeLive = isModuleEnabled && Boolean(config?.challengeSaverConfig?.enabled);

  return (
    <HStack spacing="8px" mt="6px" flexWrap="wrap">
      <Badge
        value={`Flexible Saver: ${isFlexibleLive ? 'Live' : 'Not live'}`}
        status={isFlexibleLive ? 'active' : 'locked'}
      />
      <Badge
        value={`Challenge Saver: ${isChallengeLive ? 'Live' : 'Not live'}`}
        status={isChallengeLive ? 'active' : 'locked'}
      />
    </HStack>
  );
}

function FeatureModulesTab({ companyId = '' }: FeatureModulesTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(companyId);
  const { data: companyConfiguration, isInitialLoading: isLoadingConfiguration } =
    useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyConfigAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutateAsync: updatePayCalendarAsync } = useUpdatePayCalendar({
    onSuccess: () => {},
    onError: () => {},
  });

  const {
    isOpen: isDigitalVouchersConfigOpen,
    onOpen: onOpenDigitalVouchersConfig,
    onClose: onCloseDigitalVouchersConfig,
  } = useDisclosure();

  const {
    isOpen: isSavingsConfigOpen,
    onOpen: onOpenSavingsConfig,
    onClose: onCloseSavingsConfig,
  } = useDisclosure();

  const [moduleStatuses, setModuleStatuses] = useState<Record<string, ModulesEnableOptions>>({});
  const [globalAllowancePercentageValue, setGlobalAllowancePercentageValue] = useState<
    number | string
  >(0);
  const [allPayCalendars, setAllPayCalendars] = useState<PayCalendar[]>([]);
  const [isModulesFormValid, setIsModulesFormValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: payCalendarsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isSuccess,
  } = useFetchInfinitePayCalendars({ companyId: [companyId], size: 2 }, true);

  useEffect(() => {
    if (!isSuccess) return;

    const flatList = payCalendarsData.pages.flatMap((page) => page.list);
    setAllPayCalendars(flatList);

    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [payCalendarsData, isSuccess, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const startingModuleStatuses = useMemo(() => {
    return { ...(companyConfiguration?.modulesConfig?.modules ?? {}) };
  }, [companyConfiguration]);

  useEffect(() => {
    if (companyConfiguration?.modulesConfig?.modules) {
      setModuleStatuses({ ...companyConfiguration.modulesConfig.modules });
    }
  }, [companyConfiguration]);

  useEffect(() => {
    if (moduleStatuses.RESERVE !== 'ENABLED') {
      setIsModulesFormValid(true);
    }
    if (moduleStatuses.RESERVE === 'ENABLED' && startingModuleStatuses.RESERVE !== 'ENABLED') {
      if (globalAllowancePercentageValue) {
        setIsModulesFormValid(true);
      } else {
        setIsModulesFormValid(false);
      }
    }
  }, [moduleStatuses.RESERVE, globalAllowancePercentageValue, startingModuleStatuses.RESERVE]);

  const changedModules = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(moduleStatuses).filter(
          ([module, status]) => startingModuleStatuses[module] !== status
        )
      ),
    [moduleStatuses, startingModuleStatuses]
  );
  const haveModulesChanges = Object.keys(changedModules).length > 0;

  const handleSave = async () => {
    if (!company || !haveModulesChanges) return;

    const isEnablingReserve =
      moduleStatuses.RESERVE === 'ENABLED' && startingModuleStatuses.RESERVE !== 'ENABLED';
    // A calendar with no derived payday (source=EXTERNAL, no clean rule match yet) can't be updated via this
    // route - UpdatePayCalendarRequest requires payday, same as the PUT endpoint itself does. Block the whole
    // save rather than silently skipping those calendars - RESERVE enabling successfully while some calendars
    // never got globalAllowancePercentage would leave the company in a half-configured state with no signal.
    const payCalendarsWithoutPayday = allPayCalendars.filter((payCalendar) => !payCalendar.payday);

    if (isEnablingReserve && payCalendarsWithoutPayday.length > 0) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Cannot enable Reserve yet',
        description: `${payCalendarsWithoutPayday.length} pay ${
          payCalendarsWithoutPayday.length === 1 ? 'calendar has' : 'calendars have'
        } no payday set and must be fixed before Reserve can be enabled.`,
      });
      return;
    }

    setIsSaving(true);
    try {
      if (isEnablingReserve) {
        const updatePromises = allPayCalendars.map((payCalendar) => {
          const globalAllowancePercentage = Number(globalAllowancePercentageValue);

          return updatePayCalendarAsync({
            ...payCalendar,
            payday: payCalendar.payday as Payday,
            globalAllowancePercentage,
          });
        });

        await Promise.all(updatePromises);
      }

      const companyModulesConfiguration: ModulesConfig = {
        modules: changedModules,
      };
      await updateCompanyConfigAsync({
        id: company.id,
        modulesConfig: companyModulesConfiguration,
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The feature modules were successfully saved.',
      });

      queryClient.invalidateQueries({ queryKey: ['fetchCompany', company.id] });
      queryClient.invalidateQueries({ queryKey: ['fetchCompanyConfiguration', company.id] });
    } catch (error) {
      queryClient.invalidateQueries({ queryKey: ['fetchCompanyConfiguration', company.id] });
      setModuleStatuses({ ...startingModuleStatuses });
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the feature modules.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getModuleConfigureHandler = (module: string) => {
    if (module === 'DIGITAL_VOUCHERS') {
      return onOpenDigitalVouchersConfig;
    }
    if (module === 'REWARD_AND_RECOGNITION') {
      return () => navigate(generatePath(companyPaths.recognitionSettings.path, { companyId }));
    }
    if (module === 'SAVINGS') {
      return onOpenSavingsConfig;
    }
    return undefined;
  };

  const selectModuleStatus = (module: string) => (status: ModulesEnableOptions) => {
    setModuleStatuses((previous) => ({
      ...previous,
      [module]: status,
    }));
  };

  if (isLoadingCompany || isLoadingConfiguration) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" alignItems="stretch" w="full">
      <ProfileSection title="Feature modules" isEditMode maxW="100%">
        <VStack spacing="20px" alignItems="flex-start">
          {Object.keys(companyConfiguration?.modulesConfig?.modules ?? MODULE_DISPLAY_INFO)
            .sort((first, second) => first.localeCompare(second))
            .map((module) => {
              const status = moduleStatuses[module] ?? 'UNAVAILABLE';
              const startingStatus = startingModuleStatuses[module] ?? 'UNAVAILABLE';
              const supportsProvisioning = Boolean(
                MODULE_DISPLAY_INFO[module]?.provisionBeforeEnable
              );
              return (
                <Module
                  key={module}
                  isChecked={status === 'ENABLED'}
                  isForm
                  module={module}
                  status={status}
                  onStatusSelect={selectModuleStatus(module)}
                  isDisabled={
                    (module === 'ON_DEMAND_PAY' || module === 'RESERVE') &&
                    startingStatus === 'ENABLED'
                  }
                  isEditMode
                  onGlobalAllowancePercentageValueSet={(value) =>
                    setGlobalAllowancePercentageValue(value)
                  }
                  onConfigure={
                    module === 'SAVINGS' || status !== 'UNAVAILABLE'
                      ? getModuleConfigureHandler(module)
                      : undefined
                  }
                  additionalContent={
                    module === 'SAVINGS' && status === 'ENABLED' ? (
                      <SavingsEffectiveBadges
                        moduleStatus={status}
                        config={companyConfiguration?.savingsFeatureConfig}
                      />
                    ) : undefined
                  }
                  supportsProvisioning={supportsProvisioning}
                />
              );
            })}
        </VStack>
      </ProfileSection>

      <Button
        leftIcon={<CheckOutline boxSize="20px" />}
        size="sm"
        alignSelf="flex-start"
        onClick={handleSave}
        isLoading={isSaving}
        isDisabled={!isModulesFormValid || !haveModulesChanges}
        data-testid="save-feature-modules-button"
      >
        Save changes
      </Button>

      <DigitalVouchersConfigModal
        isOpen={isDigitalVouchersConfigOpen}
        onClose={onCloseDigitalVouchersConfig}
        companyId={companyId}
        initialPointsPerSpend={
          companyConfiguration?.digitalVouchersConfig?.pointsPerDigitalVoucherSpend
        }
      />

      <SavingsConfigModal
        isOpen={isSavingsConfigOpen}
        onClose={onCloseSavingsConfig}
        companyId={companyId}
        initialConfig={companyConfiguration?.savingsFeatureConfig}
      />
    </Flex>
  );
}

export { FeatureModulesTab };
